import { useEffect, useState } from 'react';
import { api, API_BASE_URL } from '../api/client';

type State =
  | { kind: 'checking' }
  | { kind: 'ok'; version: string; environment: string }
  | { kind: 'error'; message: string };

/**
 * Proves the browser can actually reach the API.
 *
 * This is the one link in the chain that cannot be verified from a terminal:
 * CORS is enforced by the browser, so an API that answers curl perfectly can
 * still be unreachable from the page. It renders the real answer or the real
 * error -- never a reassuring placeholder.
 *
 * WHERE IT RENDERS DEPENDS ON WHAT IT SAYS, which is the design review's
 * finding 10. `API 0.1.0 · development` sat in the top bar of every screen: it
 * is developer information in a strata manager's field of view, competing for
 * attention with an overdue statutory obligation. But the unreachable state is
 * not developer information — it is the explanation for why the screen is
 * empty, and burying that in a footer would be the opposite mistake.
 *
 * So the healthy state is a quiet footer line and the broken state is a banner.
 * The component is mounted in both places and each instance renders only the
 * states that belong to it: one hook, one behaviour, and no second copy that
 * can drift from this one.
 */
export function ApiStatus({ place }: { place: 'banner' | 'footer' }) {
  const [state, setState] = useState<State>({ kind: 'checking' });

  useEffect(() => {
    let live = true;
    api
      .version()
      .then((v) => live && setState({ kind: 'ok', version: v.version, environment: v.environment }))
      .catch((e: Error) => live && setState({ kind: 'error', message: e.message }));
    return () => {
      live = false;
    };
  }, []);

  if (state.kind === 'error') {
    if (place !== 'banner') return null;
    return (
      // `alert` rather than `status`: a screen reader should interrupt for
      // this, because everything else on the page is about to be wrong.
      <p className="api-status api-status--error" role="alert">
        <strong>API unreachable</strong> — {state.message}
        <br />
        <small>{API_BASE_URL}</small>
      </p>
    );
  }

  if (place !== 'footer') return null;

  if (state.kind === 'checking') {
    return <span className="api-status">checking API…</span>;
  }

  return (
    <span className="api-status api-status--ok" role="status">
      API {state.version} · {state.environment}
    </span>
  );
}
